import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MessageCircle, Search, Users, Loader, ArrowLeft, ShoppingBag, ExternalLink, MessageSquarePlus, Shield, Maximize2, Minimize2
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import StreamChatComponent from '../components/StreamChatComponent';
import { useAuth } from '../../contexts/AuthContext';
import { useStreamChat } from '../contexts/StreamChatContext';
import { useTeammates } from '../../teams/hooks/useTeammates';
import { useDirectMessages } from '../hooks/useDirectMessages';
import { useMarketplaceMessages } from '../../marketplace/hooks/useMarketplaceMessages';
import { useGroupChats } from '../hooks/useGroupChats';
import ProfileAvatar from '../../profile/components/ProfileAvatar';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import { safeAreaTop } from '../../utils/helpers';

// `embedded` drops this component's own page chrome — the full-height
// background, the centred max-width wrapper and the "Messages" heading — so it
// can sit inside another page's content area (the academy's tab body) without
// a second page header or doubled padding. The conversation list, responsive
// list/detail switch and mobile behaviour are identical either way; only the
// outer shell changes.
const MessagesPage = ({ embedded = false, initialDmUser = null }) => {
  const { theme, currentTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, authChecked, user } = useAuth();
  const { chatClient, isConnecting: chatConnecting, connectChat, error: chatError } = useStreamChat();
  const isDark = currentTheme?.toLowerCase().includes('dark');

  // Chat connects lazily — this page (not just StreamChatComponent, which
  // only renders once a channel is already selected) needs the connection
  // itself to build the conversation list below.
  useEffect(() => {
    connectChat();
  }, [connectChat]);

  const { teammates, loading: teammatesLoading } = useTeammates();
  const { conversations: dmConversations, getOrCreateDMChannel, markAsRead: markDmAsRead } = useDirectMessages();
  const { conversations: marketplaceConversations, markAsRead: markMarketplaceAsRead } = useMarketplaceMessages();
  const { conversations: groupConversations, markAsRead: markGroupAsRead } = useGroupChats();

  // selectedConv shape: { type: 'dm'|'marketplace'|'squad', channel, ...conv fields }
  const [selectedConv, setSelectedConv] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingChannel, setIsCreatingChannel] = useState(false);
  // Full screen belongs to this page, not to the chat component inside it.
  // StreamChatComponent only knows about one conversation, so while it owned
  // the overlay there was no way to show the conversation list without leaving
  // full screen — Back had to un-zoom, which is not how a messenger behaves.
  // Owning it here means the overlay contains both panes: Back returns to the
  // list and stays zoomed; the control at top right is what exits.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const markDmAsReadRef = useRef(markDmAsRead);
  useEffect(() => { markDmAsReadRef.current = markDmAsRead; }, [markDmAsRead]);

  // Tracks which ?with= userId we've already initiated so the effect doesn't
  // re-fire when dmConversations refreshes after channel creation.
  const handledDeepLinkRef = useRef(null);
  // Same guard for marketplace deep links — prevents repeated markRead on conversation list refresh
  const handledMarketplaceDeepLinkRef = useRef(null);

  const isStillTeammate = selectedConv?.type === 'dm'
    ? teammates.some(t => String(t.user?.id) === String(selectedConv.otherUser?.id))
    : false;

  // A DM the app itself offered — the academy staff directory's Message button,
  // for one — is a legitimate contact who was never a teammate. Judging everyone
  // by the teammate list alone called those people "Former Teammate" and made
  // the composer read-only, so the one thing the button exists to do could not
  // be done. The server's create-dm-channel has no such restriction; this was
  // only ever a client-side label.
  const dmRelationship = selectedConv?.type === 'dm'
    ? (selectedConv.otherUser?.relationship || null)
    : null;
  const canComposeDm = isStillTeammate || !!dmRelationship;

  // Merge all conversations sorted by most recent activity
  const allConversations = useMemo(() => {
    const dms = dmConversations.map(c => ({ ...c, type: 'dm' }));
    const marketplace = marketplaceConversations.map(c => ({ ...c, type: 'marketplace' }));
    const groups = groupConversations.map(c => ({ ...c, type: 'squad' }));
    return [...dms, ...marketplace, ...groups]
      .filter(c => c.lastMessage != null)
      .sort((a, b) => {
        const aTime = a.lastMessage?.createdAt || a.updatedAt || 0;
        const bTime = b.lastMessage?.createdAt || b.updatedAt || 0;
        return new Date(bTime) - new Date(aTime);
      });
  }, [dmConversations, marketplaceConversations, groupConversations]);

  const filteredConversations = useMemo(() => {
    if (!searchTerm) return allConversations;
    const term = searchTerm.toLowerCase();
    return allConversations.filter(conv => {
      if (conv.type === 'dm') return `${conv.otherUser?.firstName || ''} ${conv.otherUser?.lastName || ''}`.toLowerCase().includes(term);
      if (conv.type === 'marketplace') return (conv.listingTitle || '').toLowerCase().includes(term);
      if (conv.type === 'squad') return (conv.name || '').toLowerCase().includes(term);
      return false;
    });
  }, [allConversations, searchTerm]);

  const filteredTeammates = useMemo(() => teammates.filter(t =>
    !searchTerm || `${t.user?.firstName} ${t.user?.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  ), [teammates, searchTerm]);

  // Redirect if not authenticated
  useEffect(() => {
    if (authChecked && !isAuthenticated) navigate('/signin', { replace: true });
  }, [authChecked, isAuthenticated, navigate]);

  // Deep link: ?tab=marketplace&channel=channelId
  useEffect(() => {
    const tab = searchParams.get('tab');
    const channelId = searchParams.get('channel');
    if (tab !== 'marketplace' || !channelId || !chatClient) return;
    if (handledMarketplaceDeepLinkRef.current === channelId) return;

    if (marketplaceConversations.length > 0) {
      const conv = marketplaceConversations.find(c => c.id === channelId);
      if (conv) {
        handledMarketplaceDeepLinkRef.current = channelId;
        handleSelectConv({ ...conv, type: 'marketplace' });
        return;
      }
    }

    // Watch channel directly (not in list yet)
    handledMarketplaceDeepLinkRef.current = channelId;
    const watchChannel = async () => {
      try {
        const channel = chatClient.channel('messaging', channelId);
        await channel.watch();
        const data = channel.data || {};
        const members = Object.values(channel.state.members);
        const other = members.find(m => m.user_id !== String(user?.id));
        handleSelectConv({
          type: 'marketplace',
          id: channelId,
          channel,
          listingId: data.listingId,
          listingTitle: data.listingTitle || 'Listing',
          listingPrice: data.listingPrice || 0,
          listingImage: resolveImageUrl(data.listingImage) || '',
          otherUser: { id: other?.user?.id, name: other?.user?.name || 'User', image: other?.user?.image },
          lastMessage: null,
          unreadCount: 0,
        });
        // markRead is handled by handleSelectConv → markMarketplaceAsRead
      } catch (err) { console.error('[MessagesPage] Error watching marketplace channel:', err); }
    };
    watchChannel();
  }, [searchParams, chatClient, marketplaceConversations, user?.id]);

  // Deep link: ?with=userId
  useEffect(() => {
    const withUserId = searchParams.get('with');
    if (!withUserId || !chatClient || !user || teammatesLoading) return;
    // Bail out if we already initiated this deep link — prevents re-firing
    // when dmConversations refreshes after channel creation.
    if (handledDeepLinkRef.current === withUserId) return;
    const targetTeammate = teammates.find(t => String(t.user?.id) === String(withUserId));
    if (targetTeammate) {
      handledDeepLinkRef.current = withUserId;
      handleStartDM(targetTeammate);
      return;
    }
    const existingConv = dmConversations.find(c => String(c.otherUser?.id) === String(withUserId));
    if (existingConv) {
      handledDeepLinkRef.current = withUserId;
      handleSelectConv({ ...existingConv, type: 'dm' });
    }
  }, [searchParams, chatClient, user, teammates, dmConversations, teammatesLoading]);

  // Host-supplied DM target (see initialDmUser). Same destination as the
  // ?with= deep link above, but works for anyone — the guard mirrors that
  // effect's so a re-render or a refreshed conversation list cannot reopen it.
  // Open the host-supplied DM exactly once. It is marked handled here rather
  // than after the channel exists, and that is what makes Back work: this
  // effect re-runs when chatClient connects, and an unhandled target would
  // reopen the conversation the user had just closed — which looked like Back
  // being ignored, on web as much as on device.
  const handledInitialDmRef = useRef(null);
  useEffect(() => {
    const targetId = initialDmUser?.id;
    if (!targetId || !user) return;
    if (handledInitialDmRef.current === String(targetId)) return;
    handledInitialDmRef.current = String(targetId);
    // Shown immediately, before the client is up, so tapping Message lands on
    // the person rather than on the conversation list. The thread renders its
    // own connecting state until the channel arrives.
    setSelectedConv({ type: 'dm', channel: null, otherUser: initialDmUser });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDmUser?.id, user]);

  // Give a showing DM its Stream channel once the client is available. Attempts
  // are recorded per person so a failure surfaces as an error instead of
  // retrying forever; handleCloseChat clears it, so reopening tries again.
  const dmChannelAttemptRef = useRef(null);
  useEffect(() => {
    if (!chatClient || isCreatingChannel) return;
    if (selectedConv?.type !== 'dm' || selectedConv.channel) return;
    const otherId = selectedConv.otherUser?.id;
    if (!otherId || dmChannelAttemptRef.current === String(otherId)) return;
    dmChannelAttemptRef.current = String(otherId);
    handleStartDM({ user: selectedConv.otherUser });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatClient, isCreatingChannel, selectedConv?.otherUser?.id, selectedConv?.channel, selectedConv?.type]);

  // Deep link: ?channel=channelId (from Stream push tap — no tab qualifier)
  const handledStreamPushRef = useRef(null);
  useEffect(() => {
    const channelId = searchParams.get('channel');
    const tab = searchParams.get('tab');
    if (!channelId || tab === 'marketplace' || !chatClient || !user) return;
    if (handledStreamPushRef.current === channelId) return;
    handledStreamPushRef.current = channelId;

    // Check existing conversations first
    const existingConv = [...dmConversations, ...groupConversations].find(c => c.id === channelId);
    if (existingConv) {
      handleSelectConv({ ...existingConv, type: existingConv.type || 'dm' });
      return;
    }

    // channelType param distinguishes group ('gaming') from DM ('messaging')
    const channelType = searchParams.get('channelType') || 'messaging';

    // Watch the channel directly
    const openChannel = async () => {
      try {
        const channel = chatClient.channel(channelType, channelId);
        await channel.watch();
        const members = Object.values(channel.state.members);

        if (channelType === 'gaming') {
          const data = channel.data || {};
          handleSelectConv({
            type: 'squad',
            id: channelId,
            channel,
            name: data.name || 'Squad',
            image: resolveImageUrl(data.image) || null,
            lastMessage: null,
            unreadCount: 0,
          });
        } else {
          const other = members.find(m => m.user_id !== String(user?.id));
          handleSelectConv({
            type: 'dm',
            id: channelId,
            channel,
            otherUser: { id: other?.user?.id, name: other?.user?.name || 'User', image: other?.user?.image },
            lastMessage: null,
            unreadCount: 0,
          });
        }
      } catch (err) { console.error('[MessagesPage] Stream push deep link error:', err); }
    };
    openChannel();
  }, [searchParams, chatClient, user, dmConversations, groupConversations]);

  // Create pending DM channel once chatClient is ready
  useEffect(() => {
    if (!chatClient || selectedConv?.type !== 'dm' || selectedConv.channel || isCreatingChannel) return;
    const otherUserId = selectedConv.otherUser?.id;
    if (!otherUserId) return;
    const create = async () => {
      setIsCreatingChannel(true);
      try {
        const result = await getOrCreateDMChannel(otherUserId);
        if (result.success && result.channel) {
          setSelectedConv(prev => ({ ...prev, channel: result.channel }));
          if (result.channelId) await markDmAsReadRef.current(result.channelId);
        }
      } finally { setIsCreatingChannel(false); }
    };
    create();
  }, [chatClient, selectedConv, isCreatingChannel, getOrCreateDMChannel]);

  const handleSelectConv = useCallback(async (conv) => {
    setSelectedConv(conv);
    setShowNewChat(false);
    setSearchTerm('');
    if (!conv.id) return;
    try {
      if (conv.type === 'dm') await markDmAsRead(conv.id);
      else if (conv.type === 'marketplace') await markMarketplaceAsRead(conv.id);
      else if (conv.type === 'squad') await markGroupAsRead(conv.id);
    } catch (_) {}
  }, [markDmAsRead, markMarketplaceAsRead, markGroupAsRead]);

  const handleStartDM = async (teammate) => {
    if (isCreatingChannel) return;
    setSelectedConv({ type: 'dm', channel: null, otherUser: teammate.user });
    setShowNewChat(false);
    setSearchTerm('');
    if (chatConnecting || !chatClient) return;
    setIsCreatingChannel(true);
    try {
      const result = await getOrCreateDMChannel(teammate.user.id);
      if (result.success && result.channel) {
        setSelectedConv({ type: 'dm', channel: result.channel, otherUser: teammate.user });
        if (result.channelId) await markDmAsRead(result.channelId);
      }
    } catch (err) { console.error('[MessagesPage] Error starting DM:', err); }
    finally { setIsCreatingChannel(false); }
  };

  const handleCloseChat = () => {
    dmChannelAttemptRef.current = null;
    setSelectedConv(null);
  };

  useEffect(() => {
    if (!isFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isFullscreen]);

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const diff = Date.now() - d;
    if (diff < 60000) return 'Now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d`;
    return d.toLocaleDateString();
  };

  const formatPrice = (price) => {
    if (!price) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);
  };

  if (!authChecked) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme.background}`}>
        <Loader className={`w-8 h-8 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
      </div>
    );
  }

  const hasSelection = !!selectedConv;

  return (
    <div className={embedded ? '' : `min-h-screen ${theme.background}`}>
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-6'}>
        {!embedded && (
          <div className="mb-6">
            <h1 className={`text-2xl font-bold ${theme.text}`}>Messages</h1>
            <p className={`mt-1 ${theme.textMuted}`}>All your conversations in one place</p>
          </div>
        )}

        <div
          className={`flex overflow-hidden ${isFullscreen ? '' : `rounded-2xl border ${theme.border}`} ${theme.backgroundSecondary}`}
          style={isFullscreen
            ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, paddingTop: safeAreaTop() }
            : undefined}
        >

          {/* Left panel — conversation list */}
          <div className={`${hasSelection ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 flex-shrink-0 md:border-r ${theme.border}`}>
            {/* Search + New Chat */}
            <div className="p-3 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
                <input
                  type="text"
                  placeholder={showNewChat ? 'Search teammates...' : 'Search chats...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-9 pr-4 py-2 rounded-lg border text-sm ${theme.input} placeholder:${theme.textMuted}`}
                />
              </div>
              <button
                onClick={() => { setShowNewChat(v => !v); setSearchTerm(''); }}
                title={showNewChat ? 'Back to chats' : 'New chat'}
                className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                  showNewChat
                    ? isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'
                    : `${theme.cardHover} ${theme.textMuted}`
                }`}
              >
                <MessageSquarePlus className="w-4 h-4" />
              </button>
              {isFullscreen && (
                <button
                  onClick={() => setIsFullscreen(false)}
                  title="Exit full screen"
                  aria-label="Exit full screen"
                  className={`p-2 rounded-lg transition-colors flex-shrink-0 ${theme.cardHover} ${theme.textMuted}`}
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="overflow-y-auto" style={isFullscreen ? { flex: 1, minHeight: 0 } : { maxHeight: '600px' }}>
              {showNewChat ? (
                // Teammate list to start a new DM
                teammatesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className={`w-6 h-6 animate-spin ${theme.textMuted}`} />
                  </div>
                ) : filteredTeammates.length === 0 ? (
                  <div className={`text-center py-8 px-4 ${theme.textMuted}`}>
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No teammates yet</p>
                    <p className="text-sm mt-1">Connect with other players to start chatting</p>
                  </div>
                ) : (
                  filteredTeammates.map(teammate => (
                    <button
                      key={teammate.id}
                      onClick={() => handleStartDM(teammate)}
                      disabled={isCreatingChannel}
                      className={`w-full p-4 flex items-center gap-3 transition-colors ${theme.cardHover}`}
                    >
                      <ProfileAvatar
                        userId={teammate.user?.id}
                        profileImage={teammate.user?.profileImage}
                        firstName={teammate.user?.firstName}
                        lastName={teammate.user?.lastName}
                        size="lg"
                      />
                      <div className="flex-1 min-w-0 text-left">
                        <p className={`font-medium truncate ${theme.text}`}>
                          {`${teammate.user?.firstName || ''} ${teammate.user?.lastName || ''}`.trim() || 'Unknown'}
                        </p>
                        <p className={`text-xs ${theme.textMuted}`}>Teammate</p>
                      </div>
                      <MessageCircle className={`w-4 h-4 ${theme.textMuted}`} />
                    </button>
                  ))
                )
              ) : (
                // Unified conversation list
                filteredConversations.length === 0 ? (
                  <div className={`text-center py-12 px-4 ${theme.textMuted}`}>
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No recent chats</p>
                    <p className="text-sm mt-1">Tap the pencil icon to start a conversation</p>
                  </div>
                ) : (
                  filteredConversations.map(conv => {
                    const isSelected = selectedConv?.type === conv.type && selectedConv?.id === conv.id;

                    // Build per-type display
                    let avatar;
                    let title;
                    let subtitle;
                    let badge;

                    if (conv.type === 'dm') {
                      avatar = (
                        <ProfileAvatar
                          userId={conv.otherUser?.id}
                          profileImage={conv.otherUser?.profileImage}
                          firstName={conv.otherUser?.firstName}
                          lastName={conv.otherUser?.lastName}
                          size="lg"
                        />
                      );
                      title = `${conv.otherUser?.firstName || ''} ${conv.otherUser?.lastName || ''}`.trim() || conv.otherUser?.name || 'Unknown';
                      subtitle = conv.lastMessage?.text || '';
                      badge = (
                        <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                          DM
                        </span>
                      );
                    } else if (conv.type === 'marketplace') {
                      avatar = (
                        <div className={`w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 ${theme.backgroundSecondary}`}>
                          {conv.listingImage
                            ? <img src={conv.listingImage} alt="" className="w-full h-full object-cover" onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
                            : null}
                          <div className={`w-full h-full items-center justify-center ${conv.listingImage ? 'hidden' : 'flex'}`}><ShoppingBag className={`w-5 h-5 ${theme.textMuted}`} /></div>
                        </div>
                      );
                      title = conv.listingTitle || 'Listing';
                      subtitle = conv.lastMessage?.text || conv.otherUser?.name || '';
                      badge = (
                        <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-600'}`}>
                          Market
                        </span>
                      );
                    } else {
                      avatar = (
                        <div className={`w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ${theme.backgroundSecondary} flex items-center justify-center`}>
                          {conv.image
                            ? <img src={conv.image} alt={conv.name} className="w-full h-full object-cover" />
                            : <Shield className={`w-5 h-5 ${theme.textMuted}`} />
                          }
                        </div>
                      );
                      title = conv.name || 'Squad';
                      subtitle = conv.lastMessage?.text || '';
                      badge = (
                        <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${isDark ? 'bg-purple-900/40 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                          Squad
                        </span>
                      );
                    }

                    return (
                      <button
                        key={`${conv.type}-${conv.id}`}
                        onClick={() => handleSelectConv(conv)}
                        className={`w-full p-3 flex items-center gap-3 transition-colors ${
                          isSelected
                            ? isDark ? 'bg-gray-700' : 'bg-blue-50'
                            : theme.cardHover
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          {avatar}
                          {conv.unreadCount > 0 && (
                            <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full min-w-5 h-5 flex items-center justify-center px-1">
                              {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <p className={`font-medium text-sm truncate ${theme.text}`}>{title}</p>
                              {badge}
                            </div>
                            <span className={`text-xs flex-shrink-0 ${theme.textMuted}`}>
                              {formatTime(conv.lastMessage?.createdAt || conv.updatedAt)}
                            </span>
                          </div>
                          {subtitle && (
                            <p className={`text-xs truncate mt-0.5 ${conv.unreadCount > 0 ? `${theme.text} font-medium` : theme.textMuted}`}>
                              {subtitle}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })
                )
              )}
            </div>
          </div>

          {/* Right panel — chat view */}
          <div className={`${hasSelection ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`} style={{ minHeight: '600px' }}>

            {/* Active channel with chatClient */}
            {selectedConv?.channel && chatClient ? (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden min-w-0">
                <div className={`flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b overflow-hidden ${theme.border} ${theme.backgroundSecondary}`}>
                  <button onClick={handleCloseChat} title="Back to conversations" aria-label="Back to conversations" className={`flex-shrink-0 ${isFullscreen ? '' : 'md:hidden'} p-2 -ml-2 rounded-lg ${theme.cardHover} transition-colors`}>
                    <ArrowLeft className={`w-5 h-5 ${theme.textMuted}`} />
                  </button>

                  {selectedConv.type === 'dm' && (
                    <>
                      <ProfileAvatar
                        userId={selectedConv.otherUser?.id}
                        profileImage={selectedConv.otherUser?.profileImage}
                        firstName={selectedConv.otherUser?.firstName}
                        lastName={selectedConv.otherUser?.lastName}
                        size="md"
                      />
                      <div>
                        <h3 className={`font-semibold ${theme.text}`}>
                          {`${selectedConv.otherUser?.firstName || ''} ${selectedConv.otherUser?.lastName || ''}`.trim()
                            || selectedConv.otherUser?.name
                            || 'Unknown'}
                        </h3>
                        <p className={`text-xs ${canComposeDm ? theme.textMuted : 'text-amber-500'}`}>
                          {dmRelationship || (isStillTeammate ? 'Teammate' : 'Former Teammate')}
                        </p>
                      </div>
                    </>
                  )}

                  {selectedConv.type === 'marketplace' && (
                    <>
                      <div className={`w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 ${theme.backgroundSecondary}`}>
                        {selectedConv.listingImage
                          ? <img src={selectedConv.listingImage} alt="" className="w-full h-full object-cover" onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
                          : null}
                        <div className={`w-full h-full items-center justify-center ${selectedConv.listingImage ? 'hidden' : 'flex'}`}><ShoppingBag className={`w-5 h-5 ${theme.textMuted}`} /></div>
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className={`font-semibold text-sm truncate ${theme.text}`}>{selectedConv.listingTitle}</h3>
                        <div className={`flex items-center gap-1 text-xs ${theme.textMuted}`}>
                          <span>{formatPrice(selectedConv.listingPrice)}</span>
                          <span>&middot;</span>
                          <span>{selectedConv.otherUser?.name}</span>
                        </div>
                      </div>
                      {selectedConv.listingId && (
                        <button
                          onClick={() => navigate(`/marketplace/listing/${selectedConv.listingId}`)}
                          className={`flex-shrink-0 p-2 rounded-lg ${theme.cardHover} ${theme.textMuted} transition-colors`}
                          title="View listing"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}

                  {selectedConv.type === 'squad' && (
                    <>
                      <div className={`w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ${theme.backgroundSecondary} flex items-center justify-center`}>
                        {selectedConv.image
                          ? <img src={selectedConv.image} alt={selectedConv.name} className="w-full h-full object-cover" />
                          : <Shield className={`w-5 h-5 ${theme.textMuted}`} />
                        }
                      </div>
                      <div>
                        <h3 className={`font-semibold ${theme.text}`}>{selectedConv.name || 'Squad'}</h3>
                        <p className={`text-xs ${theme.textMuted}`}>Squad Chat</p>
                      </div>
                    </>
                  )}

                  <button
                    onClick={() => setIsFullscreen(v => !v)}
                    title={isFullscreen ? 'Exit full screen' : 'Expand to full screen'}
                    aria-label={isFullscreen ? 'Exit full screen' : 'Expand to full screen'}
                    className={`ml-auto flex-shrink-0 p-2 rounded-lg ${theme.cardHover} transition-colors`}
                  >
                    {isFullscreen
                      ? <Minimize2 className={`w-5 h-5 ${theme.textMuted}`} />
                      : <Maximize2 className={`w-5 h-5 ${theme.textMuted}`} />}
                  </button>
                </div>

                <StreamChatComponent
                  // This page owns full screen now, so the component must not
                  // render a second overlay or a competing expand button.
                  allowFullscreen={false}
                  fillParent={isFullscreen}
                  channel={selectedConv.channel}
                  channelName={`${selectedConv.otherUser?.firstName || ''} ${selectedConv.otherUser?.lastName || ''}`.trim() || undefined}
                  height={540}
                  readOnly={selectedConv.type === 'dm' && !canComposeDm}
                  readOnlyMessage="You are no longer teammates. Past messages are still visible."
                />
              </div>
            ) : selectedConv && (chatConnecting || !chatClient || isCreatingChannel) ? (
              // Loading state for pending DM creation
              <div className={`flex-1 flex flex-col min-h-0 ${theme.backgroundSecondary}`}>
                <div className={`flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b ${theme.border}`}>
                  <button onClick={handleCloseChat} title="Back to conversations" aria-label="Back to conversations" className={`${isFullscreen ? '' : 'md:hidden'} p-2 -ml-2 rounded-lg ${theme.cardHover}`}>
                    <ArrowLeft className={`w-5 h-5 ${theme.textMuted}`} />
                  </button>
                  {selectedConv.otherUser && (
                    <>
                      <ProfileAvatar
                        userId={selectedConv.otherUser?.id}
                        profileImage={selectedConv.otherUser?.profileImage}
                        firstName={selectedConv.otherUser?.firstName}
                        lastName={selectedConv.otherUser?.lastName}
                        size="md"
                      />
                      <h3 className={`font-semibold ${theme.text}`}>
                        {`${selectedConv.otherUser?.firstName || ''} ${selectedConv.otherUser?.lastName || ''}`.trim()
                          || selectedConv.otherUser?.name}
                      </h3>
                    </>
                  )}
                </div>
                <div className="flex-1 flex items-center justify-center px-6">
                  {/* StreamChatContext records why a connection failed but nothing
                      read it, so a failed connect was indistinguishable from a slow
                      one — an endless "Loading conversation..." with the cause only
                      in the console. */}
                  {chatError && !chatConnecting ? (
                    <div className="text-center">
                      <p className={`text-lg font-medium mb-1 ${theme.text}`}>Couldn't open this chat</p>
                      <p className={`text-sm mb-4 ${theme.textMuted}`}>{chatError}</p>
                      <button onClick={connectChat} className={`px-4 py-2 rounded-lg text-sm font-medium ${theme.buttonPrimary || 'bg-blue-600 text-white'}`}>
                        Try again
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Loader className={`w-8 h-8 animate-spin mx-auto mb-4 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                      <p className={`text-lg font-medium ${theme.textSecondary}`}>
                        {chatConnecting ? 'Connecting to chat...' : 'Loading conversation...'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : chatConnecting ? (
              <div className={`hidden md:flex flex-1 items-center justify-center ${theme.backgroundSecondary}`}>
                <div className="text-center">
                  <Loader className={`w-8 h-8 animate-spin mx-auto mb-4 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                  <p className={`text-lg font-medium ${theme.textSecondary}`}>Connecting to chat...</p>
                </div>
              </div>
            ) : (
              <div className={`hidden md:flex flex-1 items-center justify-center ${theme.backgroundSecondary}`}>
                <div className="text-center">
                  <MessageCircle className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted} opacity-50`} />
                  <p className={`text-lg font-medium ${theme.textSecondary}`}>Select a conversation</p>
                  <p className={`text-sm ${theme.textMuted}`}>Choose a chat from the list or start a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
